/**
 * Centralized query keys for TanStack Query.
 *
 * These are organized by API resource (not feature) to make
 * cross-feature invalidation straightforward.
 *
 * Usage:
 *   queryClient.invalidateQueries({ queryKey: queryKeys.posts.all })
 *   // invalidates all post data (drafts, queued, archives)
 *
 *   queryClient.invalidateQueries({ queryKey: ["posts", "drafts"] })
 *   // invalidates all drafts across all accounts
 */

import type { components } from "./generated/sked-api-openapi-types";

/** Content Collect list filter — the PERSISTED status, not the derived state. */
type ContentCollectStatusFilter = components["schemas"]["ContentCollectStatus"];
type ContentCollectSubmissionStatusFilter =
  components["schemas"]["ContentCollectSubmissionStatus"];

export const queryKeys = {
  posts: {
    all: ["posts"] as const,
    drafts: (accountId: string) => ["posts", "drafts", accountId] as const,
    queued: (accountId: string) => ["posts", "queued", accountId] as const,
    archives: (accountId: string) => ["posts", "archives", accountId] as const,
  },
  approvals: {
    all: ["approvals"] as const,
    external: {
      all: ["approvals", "external"] as const,
      links: ["approvals", "external", "links"] as const,
      themes: ["approvals", "external", "themes"] as const,
    },
    boardPosts: {
      all: ["approvals", "boardPosts"] as const,
      list: (params: {
        accountIds: string[];
        postContentTypes: string[];
        postStatusKeys: string[] | null;
        start: number;
        end: number;
        mode?: "upcoming" | "drafts";
        timezone?: string;
      }) =>
        [
          "approvals",
          "boardPosts",
          params.accountIds,
          params.postContentTypes,
          params.postStatusKeys ?? [],
          params.start,
          params.end,
          params.mode ?? "upcoming",
          params.timezone ?? "",
        ] as const,
    },
    comments: (channelId: string) =>
      ["approvals", "comments", channelId] as const,
    collaborators: ["approvals", "collaborators"] as const,
  },
  collaboration: {
    all: ["collaboration"] as const,
    comments: (channelId: string) =>
      ["collaboration", "comments", channelId] as const,
    collaborators: ["collaboration", "collaborators"] as const,
    unreadsBase: ["collaboration", "unreads"] as const,
    unreads: (channelIds: string[]) =>
      ["collaboration", "unreads", channelIds] as const,
  },
  contentLabels: {
    all: (userId: string) => ["content-labels", "all", userId] as const,
  },
  calendar: {
    all: ["calendar"] as const,
    postLists: ["calendar", "posts"] as const,
    draftLists: ["calendar", "drafts"] as const,
    bttp: (accountId: string) =>
      ["calendar", "best-time-to-post", accountId] as const,
    posts: (params: {
      accountIds: string[];
      start: number;
      end: number;
      postContentTypes: string[];
      postStatusKeys: string[] | null;
      /** Optional here only so existing test call sites keep compiling. The
       * response depends on it — see CalendarPostsQueryParams, which requires
       * it and is what production callers actually build. */
      timezone?: string;
    }) => ["calendar", "posts", params] as const,
    notes: (params: { accounts: string[]; start: number; end: number }) =>
      ["calendar", "notes", params] as const,
    ideas: (groupIds: string[]) => ["calendar", "ideas", groupIds] as const,
    drafts: (params: {
      accountIds: string[];
      postStatusKeys: string[] | null;
      postContentTypes: string[];
      labelIds?: string[] | null;
      sort: "asc" | "desc";
      pageSize: number;
      searchText?: string;
    }) => ["calendar", "drafts", params] as const,
    postCluster: (clusterId: string) =>
      ["calendar", "post-cluster", clusterId] as const,
    holidayCountries: ["calendar", "holiday-countries"] as const,
    holidayCalendars: ["calendar", "holiday-calendars"] as const,
    holidayEvents: (calendarIds: string[]) =>
      ["calendar", "holiday-events", calendarIds] as const,
  },
  contentCollects: {
    all: ["content-collects"] as const,
    lists: ["content-collects", "list"] as const,
    list: (params: {
      q?: string;
      status?: ContentCollectStatusFilter;
      page?: number;
      limit?: number;
    }) => ["content-collects", "list", params] as const,
    detail: (collectId: string) =>
      ["content-collects", "detail", collectId] as const,
    submissions: {
      all: (collectId: string) =>
        ["content-collects", "submissions", collectId] as const,
      list: (
        collectId: string,
        params: {
          page?: number;
          limit?: number;
          status?: ContentCollectSubmissionStatusFilter;
        }
      ) => ["content-collects", "submissions", collectId, params] as const,
      detail: (collectId: string, submissionId: string) =>
        [
          "content-collects",
          "submissions",
          collectId,
          "detail",
          submissionId,
        ] as const,
    },
    /** SKE-4868: the async "download all files" archive build. */
    exports: {
      detail: (collectId: string, exportId: string) =>
        ["content-collects", "exports", collectId, exportId] as const,
    },
  },
  /**
   * The admin's Library collections (`content-inbox` inboxes), read by
   * react-query consumers such as the Content Collect destination picker. The
   * Media Library screen itself still holds collections in redux; this key is
   * for the react-query readers that only need the list.
   */
  libraryCollections: {
    all: ["library-collections"] as const,
    list: ["library-collections", "list"] as const,
    tags: (collectionId: string) =>
      ["library-collections", "tags", collectionId] as const,
  },
  gridPlans: {
    all: ["grid-plans"] as const,
    list: (accountId: string) => ["grid-plans", "list", accountId] as const,
    detail: (gridId: string) => ["grid-plans", "detail", gridId] as const,
    heatmap: (accountId: string) =>
      ["grid-plans", "heatmap", accountId] as const,
    applyPreview: (gridId: string, mode: string) =>
      ["grid-plans", "apply-preview", gridId, mode] as const,
  },
  accounts: {
    all: ["accounts"] as const,
    settings: {
      queue: (accountId: string) =>
        ["accounts", accountId, "settings", "queue"] as const,
    },
  },
};
